// Implements: F-CNV-003, F-MCK-001
/**
 * The POST that starts a conversation turn from the composer of a mockup (`conversation`).
 * The creation of a mockup starts its first turn in its own call (`POST /mockups/conversation`,
 * 7.6), so nothing else sends here; the call stays in `shared/api` beside the stream-follow helper.
 *
 * Starting a turn is paid and not idempotent from the network's point of view, so it is
 * never retried (`retry: false`): a lost response must reach the person, not run the turn
 * twice. The client's own `turn_id` is the server's idempotency key.
 */

import { ApiError, UNKNOWN_ERROR_CODE } from "./ApiError";
import { conversationPath } from "./conversationPath";
import { httpRequest } from "./httpClient";
import type { TurnAccepted, TurnMode } from "../../types/api";

export interface StartTurnInput {
  readonly text: string;
  /** The client's own turn id (the server's idempotency key). */
  readonly turnId: string;
  /** Only what the person chose: absent means the server's default (F-CNV-010). */
  readonly mode?: TurnMode | undefined;
  /** Ask to review the brief before anything is drawn (F-CNV-016): sent only when `true`. */
  readonly reviewBrief?: boolean | undefined;
  /** The reference image as plain base64 (F-CNV-012): sent only when there is one. */
  readonly imageBase64?: string | undefined;
  readonly signal?: AbortSignal | undefined;
}

export async function startTurn(mockupId: string, input: StartTurnInput): Promise<TurnAccepted> {
  const body = await httpRequest<TurnAccepted>(
    `${conversationPath(mockupId)}/turns`,
    {
      method: "POST",
      body: {
        text: input.text,
        turn_id: input.turnId,
        ...(input.mode === undefined ? {} : { mode: input.mode }),
        ...(input.reviewBrief === true ? { review_brief: true } : {}),
        ...(input.imageBase64 === undefined ? {} : { image_base64: input.imageBase64 }),
      },
      signal: input.signal,
      retry: false,
    },
  );
  if (body === undefined) {
    throw new ApiError(202, UNKNOWN_ERROR_CODE, "POST turns answered with no body", new Headers());
  }
  return body;
}
